import type { NormalizedJobState } from "./pipelineHelpers";

export interface JobControlState {
  /**
   * State to render in the badge. While a start/stop is converging this is the
   * transitional "Starting"/"Stopping" rather than the lagging backend `state`.
   */
  displayState: NormalizedJobState;
  startDisabled: boolean;
  stopDisabled: boolean;
}

/**
 * Derive the Start/Stop button and badge state from the job's reported `state`
 * and its `desired_state`.
 *
 * The backend flips `desired_state` immediately on a start/stop request, but
 * `state` only catches up once the change is actually applied — which is
 * asynchronous in HA deployments. Earlier code drove the buttons off `state`
 * alone (with a client-only optimistic flag that the post-mutation refetch
 * promptly overwrote), so the buttons flickered back to actionable while the
 * job was still transitioning and users had to click Start/Stop repeatedly.
 *
 * Treating the `state` ≠ `desired_state` divergence as the transition keeps the
 * buttons disabled and the badge honest until the backend converges, regardless
 * of refetch timing. `pending` covers the brief gap before the first refetch.
 */
export function deriveJobControlState(
  state: NormalizedJobState,
  desiredState: NormalizedJobState,
  pending: { starting?: boolean; stopping?: boolean } = {},
): JobControlState {
  const isFailed = state === "Failed";
  // A failed job is settled (not converging) so it stays restartable.
  const converging = !isFailed && state !== desiredState;
  const wantsRunning = desiredState === "Running";

  let displayState: NormalizedJobState = state;
  if (converging && state !== "Starting" && state !== "Stopping") {
    displayState = wantsRunning ? "Starting" : "Stopping";
  }

  return {
    displayState,
    startDisabled:
      converging || (wantsRunning && !isFailed) || !!pending.starting,
    // A failed job is not running, so Stop is meaningless — only Start (retry)
    // applies. Disable Stop whenever the job isn't (heading toward) running.
    stopDisabled: converging || !wantsRunning || isFailed || !!pending.stopping,
  };
}
